import React, {useCallback, useEffect} from 'react';
import {TreatmentHoldInput} from "./TreatmentHoldFormModal";
import {FormProvider, UseFormReturn, useFormContext} from "react-hook-form";
import {HoldTypeInput} from "./HoldTypeInput";
import {RequestReasonInput} from "./RequestReasonInput";
import {ModalDatePicker} from "../../form-components/ModalDatePicker";
import {ModalTextAreaInput} from "../../form-components/ModalTextAreaInput";
import {FatOutlineButton} from "../../../../common/component/FatOutlineButton";
import {LoadingSpinner} from "../../../../common";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import _ from "lodash";
import {Subject} from "rxjs";
import {usePreSubmitValidation} from "../ClientOfferValidation";
import {useTreatmentHoldValidation} from "./TreatmentHoldSubmitRequestValidation";
import {TreatmentHoldWorkflowFields} from "./TreatmentHoldWorkflowFields";
import {TreatmentHoldConsultationFields} from "./TreatmentHoldConsultationFields";
import {AuthorizationInfo} from "../../domain";

type TreatmentHoldFormProps = {
    visible$: Subject<boolean>;
    methods: UseFormReturn<TreatmentHoldInput>;
    onFinished: (formValues: TreatmentHoldInput | null) => Promise<void>;
    onCancel: () => Promise<void>;
    externalLoading: boolean;
    authorizationGuid?: string;
    authorization?: AuthorizationInfo;
    skipValidation?: boolean;
}

function RequestReasonOtherField({disabled}: { disabled: boolean }) {
    const {watch, setValue} = useFormContext<TreatmentHoldInput>();
    const isOther = watch('requestReason') === 'Other';

    useEffect(() => {
        if (!isOther) {
            setValue('requestReasonOther', '');
        }
    }, [isOther, setValue]);

    if (!isOther) {
        return null;
    }

    return (
        <ModalTextAreaInput
            disabled={disabled}
            field="requestReasonOther"
            label="Request Reason Other"
            labelPosition="left"
            rowsNum={3}
            maxLength={2048}
            required
            showCharacterCount
            requiredMessage="This field is required."
        />
    );
}

export function TreatmentHoldForm({
                                       visible$,
                                       methods,
                                       externalLoading,
                                       onFinished,
                                       onCancel,
                                       authorizationGuid,
                                       authorization,
                                       skipValidation = false,
                                   }: TreatmentHoldFormProps) {
    const submitWithTracking = useCallback(
        (formValues: TreatmentHoldInput | null) => trackPromise(onFinished(formValues)),
        [onFinished]
    );

    const {promiseInProgress} = usePromiseTracker();
    const {validateTreatmentHold} = useTreatmentHoldValidation();
    const {validateBeforeSubmit} = usePreSubmitValidation();

    const isCallingBackend = promiseInProgress || externalLoading;

    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: TreatmentHoldInput) => {
            const onSubmitSuccess = async () => {
                await submitWithTracking(data);
                methods.reset();
                visible$.next(false);
            };

            if (skipValidation) {
                await onSubmitSuccess();
            } else {
                await validateBeforeSubmit(
                    () => validateTreatmentHold(authorizationGuid),
                    onSubmitSuccess
                );
            }
        })(e);
    }, [methods, visible$, submitWithTracking, authorizationGuid, validateBeforeSubmit, validateTreatmentHold, skipValidation]);

    return (
        <FormProvider {...methods}>
            <form autoComplete="off" onSubmit={submit}>
                {!isCallingBackend && (<>
                    <div className="space-y-6">
                        <div className="flex flex-col w-full p-4 scrollbar-always"
                             style={{maxHeight: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"}}>

                            <div className='my-1'></div>
                            <HoldTypeInput sending={isCallingBackend}/>
                            <RequestReasonInput sending={isCallingBackend}/>
                            <RequestReasonOtherField disabled={isCallingBackend}/>

                            <ModalDatePicker<TreatmentHoldInput>
                                disabled={isCallingBackend}
                                label={'Start Date'}
                                field={'startDate'}
                                optionalRules={{required: 'This field is required.'}}
                            />

                            <ModalDatePicker<TreatmentHoldInput>
                                disabled={isCallingBackend}
                                label={'End Date'}
                                field={'endDate'}
                                bottomError={(errorMessage) => <div className='flex'>
                                    <div className="w-2/6"></div>
                                    <div className="w-4/6 pl-4 text-red-700">
                                        {errorMessage}
                                    </div>
                                </div>}
                                optionalRules={{
                                    validate: {
                                        afterStartDate: () => {
                                            const start = methods.getValues("startDate");
                                            const end = methods.getValues("endDate");
                                            if (!end) {
                                                return 'This field is required.';
                                            }
                                            if (!start) {
                                                return true;
                                            }
                                            if (new Date(start) > new Date(end)) {
                                                return 'Must be on or after the start date';
                                            }
                                            return true;
                                        }
                                    },
                                }}
                            />

                            <ModalDatePicker<TreatmentHoldInput>
                                disabled={isCallingBackend}
                                label={'Date Hold Confirmed with Caregiver/Client'}
                                field={'dateHoldDiscussedWithFamily'}
                                optionalRules={{required: 'This field is required.'}}
                            />


                            <TreatmentHoldWorkflowFields
                                sending={isCallingBackend}
                                authorization={authorization}
                            />

                            {/*<ModalDatePicker<TreatmentHoldInput>*/}
                            {/*    disabled={isCallingBackend}*/}
                            {/*    label={'Date of Most Recent Appointment'}*/}
                            {/*    field={'dateOfMostRecentAppointment'}*/}
                            {/*    optionalRules={{required: 'This field is required.'}}*/}
                            {/*/>*/}

                            <TreatmentHoldConsultationFields sending={isCallingBackend}/>

                        </div>
                    </div>

                    <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                        <div className="flex w-1/4">
                            <FatOutlineButton color={'red'}
                                              text={"Cancel"}
                                              onClick={onCancel}/>
                            <span style={{width: 10}}/>
                            <FatOutlineButton type="submit" text={"Submit"} color={"green"}/>
                        </div>
                    </div>
                </>)}

                {isCallingBackend &&
                    <div className="p-24">
                        <LoadingSpinner text="Processing"/>
                    </div>
                }
            </form>
        </FormProvider>
    );
}
